import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import billingService from '../../services/billingService';
import practiceService from '../../services/practiceService';
import { toast } from 'react-toastify';
import SearchBar from '../../components/Common/SearchBar/SearchBar';
import Table from '../../components/Common/Table/Table';
import Heading from '../../components/Common/Heading/Heading';
import './BillingCycleReport.css';

const BillingCycleReport = () => {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const { updateBreadcrumbs } = useBreadcrumb();

  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportDates, setReportDates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPractice();
    fetchReportDates();
  }, [practiceId]);

  const fetchPractice = async () => {
    try {
      const response = await practiceService.getPractice(practiceId);
      if (response.data && response.data.success) {
        const practiceData = response.data.data;
        setPractice(practiceData);
        updateBreadcrumbs([
          { label: 'Billing & Reports', path: '/billing' },
          { label: `${practiceData.practice_name || 'Practice'} - Billing Cycle Report`, path: null, active: true }
        ]);
      }
    } catch (error) {
      console.error('Error fetching practice:', error);
    }
  };

  const fetchReportDates = async () => {
    if (!practiceId) return;

    try {
      setLoading(true);
      const response = await billingService.getBillingReports(practiceId);
      if (response.success && response.data.reportDates) {
        setReportDates(response.data.reportDates);
      }
    } catch (error) {
      console.error('Error fetching report dates:', error);
      toast.error('Failed to load billing cycle report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  const filteredReportDates = reportDates.filter(rd => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      formatDate(rd.rpm_start_date).toLowerCase().includes(searchLower) ||
      formatDate(rd.rpm_end_date).toLowerCase().includes(searchLower) ||
      (rd.note && rd.note.toLowerCase().includes(searchLower)) ||
      rd.number_of_days?.toString().includes(searchLower)
    );
  });

  return (
    <div className="billing-cycle-report-wrapper">
      <header className="billing-cycle-header">
        <Heading
          title={`${practice?.practice_name || 'Practice'} Billing Cycle Report`}
          level="h2"
          className="billing-cycle-title"
        />
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          className="billing-cycle-search"
        />
      </header>

      <div className="billing-cycle-container">
        <div className="table-wrap">
          <Table
            columns={[
              {
                key: 'rowNo',
                title: '#',
                className: 'reusable-table-centered-col',
                render: (_row, rowIndex) => rowIndex + 1
              },
              { key: 'rpm_start_date', title: 'Rpm Start Date', render: (row) => formatDate(row.rpm_start_date) },
              { key: 'rpm_end_date', title: 'Rpm End Date', render: (row) => formatDate(row.rpm_end_date) },
              { key: 'note', title: 'Billing Notes', render: (row) => row.note || 'Empty Note' },
              { key: 'number_of_days', title: 'Number of Days', render: (row) => row.number_of_days || 0 }
            ]}
            data={filteredReportDates}
            isLoading={loading}
            loadingMode="skeleton"
            skeletonRows={8}
            emptyMessage="No billing cycle data available"
          />
        </div>
      </div>
    </div>
  );
};

export default BillingCycleReport;

